"use strict";

import Immutable from "immutable";
import _ from "lodash";
import { nanoid } from "nanoid";

import Block from "./Block";
import NameSlugFormatter from "../utils/NameSlugFormatter.js";
import AppConstants from "../constants/AppConstants";
import { captureExceptionPostHog } from "../utils/captureException";

const { ID_LENGTH } = AppConstants;

// once per session: the first plain write's stack identifies the source
let reportedPlainData = false;

/**
 * @param {Object} value - the non-Immutable data written to a container
 * @param {string} [conversionError]
 */
const reportPlainData = (value, conversionError) =>
  captureExceptionPostHog(new Error("BlockContainer received non-Immutable data"), {
    containerType: value.type,
    containerId: value.id,
    dataType: Object.prototype.toString.call(value),
    conversionError,
  });

class BlockContainer {
  #data;

  constructor(/** Immutable **/ data) {
    this.data = data;
  }

  get data() {
    return this.#data;
  }

  /**
   * Plain objects reach here in production ("getIn is not a function"): convert them and report
   * the source. The JSON round trip also covers objects `fromJS` skips (class instances, other realms).
   * @param {Immutable.Map|Object|null} value
   */
  set data(value) {
    if (value == null || Immutable.Iterable.isIterable(value)) {
      this.#data = value;
      return;
    }

    try {
      this.#data = Immutable.fromJS(JSON.parse(JSON.stringify(value)));
    } catch (error) {
      // circular or BigInt data can't be converted: report regardless, keep the previous data
      reportPlainData(value, error.message);
      throw error;
    }

    if (!reportedPlainData) {
      reportedPlainData = true;
      reportPlainData(value);
    }
  }

  /**
   * Check the structure of the blocks within the container
   */
  validate() {
    let blocks = this.getBlocks();

    if (blocks) {
      blocks.forEach(function (block) {
        let states = block.get("states", false);
        if (states && !Immutable.List.isList(states)) {
          console.error("Block", block.get("id", "unknown"), "Invalid States");
        }
      });
    }
  }

  isDataValid() {
    return Immutable.Map.isMap(this.data);
  }

  /**
   * Adds a new block to the block container
   * @param {Object} newBlockData
   * @param {Object} config
   * @param {Immutable.Map} additionalBlocks
   * @returns {Immutable.Map}
   */
  addBlock(newBlockData, config, additionalBlocks = Immutable.Map()) {
    var insertAt = _.get(config, "insertAt", false);

    var newBlock = _.cloneDeep(newBlockData);

    var blocks = this.getBlocks();
    var blockOrder = this.getBlockOrder();

    // merging page blocks with canvas set blocks
    const allBlocksForUniqueness = blocks ? blocks.merge(additionalBlocks) : additionalBlocks;

    const blockInfo = NameSlugFormatter.format(newBlock.name, allBlocksForUniqueness);
    const blockId = this._getBlockId(newBlockData);

    newBlock.id = blockId;
    newBlock.name = blockInfo.name;
    newBlock.slug = blockInfo.slug;

    newBlock = Immutable.fromJS(newBlock);

    //Remove any selected elements or states
    let blockEnt = new Block(newBlock, this);
    blockEnt.clearMarked();

    newBlock = blockEnt.data;

    if (insertAt !== false) {
      blockOrder = blockOrder.splice(insertAt, 0, blockId);
    } else {
      blockOrder = blockOrder.push(blockId);
    }

    blocks = blocks.set(blockId, newBlock);

    this.data = this.data.set("blockData", blocks);
    this.data = this.data.set("blocks", blockOrder);

    return newBlock;
  }

  deleteBlock(blockId) {
    var block = this.getBlock(blockId);
    var blocks = this.getBlocks();
    var blockOrder = this.getBlockOrder();

    var result = {
      index: -1,
    };

    var blockIndex = blockOrder.indexOf(blockId);
    if (block && blockIndex >= 0) {
      blockOrder = blockOrder.delete(blockIndex);
      blocks = blocks.delete(blockId);

      this.data = this.data.set("blocks", blockOrder);
      this.data = this.data.set("blockData", blocks);

      result.index = blockIndex;
      result.block = block;
    }

    return result;
  }

  /**
   * Overwrites all of the block data for the specified block exception id, name, slug
   * @param blockId
   * @param blockData
   * @return the updated block
   */
  overwriteBlock(blockId, blockData) {
    var block = this.getBlock(blockId);
    if (block) {
      blockData.name = block.get("name");
      blockData.id = block.get("id");
      blockData.slug = block.get("slug");

      var newBlock = Immutable.fromJS(blockData);

      this.data = this.data.setIn(["blockData", blockId], newBlock);

      block = this.getBlock(blockId);
    }

    return block;
  }

  get id() {
    return this.isDataValid() ? this.data.get("id") : null;
  }

  get type() {
    return this.isDataValid() ? this.data.get("type") : null;
  }

  get name() {
    return this.data.get("name");
  }

  /**
   * Updates the name of a block
   * @param {string} blockId - The id of the block/canvas to update
   * @param {string} name - The new name of the block/canvas
   * @param {Immutable.Map} additionalBlocks - Additional blocks/canvases to merge with the block/canvas
   * @returns {Block} - updated block/canvas
   */
  updateBlockName(blockId, name, additionalBlocks = Immutable.Map()) {
    var block = this.getBlock(blockId);

    if (block) {
      const allBlocksForUniqueness = this.getBlocks() ? this.getBlocks().merge(additionalBlocks) : additionalBlocks;
      
      var nameAndSlug = NameSlugFormatter.format(name, allBlocksForUniqueness, block);
      this.data = this.data.mergeIn(["blockData", blockId], nameAndSlug);

      block = this.getBlock(blockId);
    }

    return block;
  }

  updateBlockPosition(blockId, targetIndex) {
    var blockIndex = this.getBlockIndex(blockId);

    if (blockIndex != -1 && blockIndex != targetIndex) {
      var blockOrder = this.getBlockOrder();

      blockOrder = blockOrder.splice(blockIndex, 1);

      blockOrder = blockOrder.splice(targetIndex, 0, blockId);

      this.data = this.data.set("blocks", blockOrder);
    }
  }

  /**
   * Wraps block data into a Block Entity
   * @param blockId
   * @returns Block
   */
  getBlockEntity(blockId) {
    let blockData = this.getBlock(blockId);
    let block = null;
    if (blockData) {
      block = new Block(blockData, this);
    }

    return block;
  }

  /**
   * Looks at path array and returns a block based on it
   * @param Array path
   */
  getBlockEntityByPath(/** Array **/ path) {
    let blockId = null;
    if (path && path.length > 0) {
      blockId = path[0];
    }

    return this.getBlockEntity(blockId);
  }

  /**
   * Takes an Block and updates the container with the data from the block
   * @param block
   */
  setBlock(/** Block **/ block) {
    this.data = this.data.setIn(["blockData", block.id], block.data);
  }

  getBlock(blockId) {
    return this.data.getIn(["blockData", blockId]);
  }

  hasBlock(blockId) {
    return this.data.hasIn(["blockData", blockId]);
  }

  getBlockAtIndex(index) {
    let blockId = this.data.getIn(["blocks", index]);
    return this.getBlock(blockId);
  }

  getBlockID(index) {
    return this.data.getIn(["blocks", index]);
  }

  getBlockIndex(blockId) {
    return this.data.get("blocks").indexOf(blockId);
  }

  getBlocks() {
    return this.data.getIn(["blockData"]);
  }

  getBlockOrder() {
    return this.data.getIn(["blocks"]);
  }

  getBlockList() {
    let blockOrder = this.data.get("blocks");
    let blocks = Immutable.List().asMutable();

    if (blockOrder && blockOrder.size > 0) {
      let blockData = this.data.get("blockData");

      blockOrder.forEach(function (blockId) {
        blocks.push(blockData.get(blockId));
      });
    }

    return blocks.asImmutable();
  }

  isWordpressAllowed() {
    //wordpress properties allowed for site blocks and pages that have the 'wp' property
    return (
      "site" == this.data.get("type") || ("page" == this.data.get("type") && this.data.has("wp"))
    );
  }

  _generateBlockID() {
    let id = null;
    let unique = false;

    while (!unique) {
      id = nanoid(ID_LENGTH);
      unique = this._isUniqueID(id);
    }

    return id;
  }

  _isUniqueID(id) {
    if (!this.data.get("blocks").includes(id)) {
      return true;
    }
    return false;
  }

  _getBlockId(block) {
    if (_.isEmpty(block.id)) {
      return this._generateBlockID();
    }

    // determine if this was a duplicate/copy operation
    if (!this._isUniqueID(block.id)) {
      return this._generateBlockID();
    }

    return block.id;
  }
}

export default BlockContainer;
